import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Send, TestTube2, Eye, Save, Copy, Trash2, Plus,
  ArrowUp, ArrowDown, RefreshCw, Circle, ChevronDown,
} from "lucide-react";

/**
 * /admin/discord — Discord webhook management panel.
 * Admin-only (server enforces via require_admin on every endpoint).
 * All UI here uses the shared builder in /backend/discord/discord_webhook.py
 * via the /preview endpoint — so preview == real message.
 */

const emptyPayload = () => ({
  update_type: "deploy",
  title: "",
  version: "",
  status: "Pubblicato",
  description: "",
  changes: [],
  app_url: "",
  color: null,
  footer: "",
  timestamp_iso: "",
});

function colorHex(intVal) {
  if (intVal === null || intVal === undefined) return "#6E8B3D";
  return "#" + intVal.toString(16).padStart(6, "0");
}

export default function AdminDiscord() {
  const navigate = useNavigate();
  const [gate, setGate] = useState("checking"); // checking | ok | denied
  const [status, setStatus] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState(emptyPayload());
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  // ── Auth gate (client) — server always re-checks ──
  useEffect(() => {
    api.moderationMe().then(m => {
      if (m?.is_admin) setGate("ok");
      else setGate("denied");
    }).catch(() => setGate("denied"));
  }, []);

  useEffect(() => {
    if (gate !== "ok") return;
    Promise.all([
      api.discordStatus().catch(() => null),
      api.discordDrafts().catch(() => ({ drafts: [] })),
      api.discordHistory(50).catch(() => ({ history: [] })),
    ]).then(([s, d, h]) => {
      setStatus(s);
      setDrafts(d?.drafts || []);
      setHistory(h?.history || []);
    });
  }, [gate]);

  const updateTypeRow = useMemo(() => {
    const options = status?.options?.update_types || [];
    return options.find(o => o.key === form.update_type) || options[0] || null;
  }, [status, form.update_type]);

  const effectiveColorHex = colorHex(
    form.color !== null && form.color !== undefined
      ? form.color
      : (updateTypeRow?.default_color ?? 0x6E8B3D)
  );

  const refreshAll = async () => {
    const [s, d, h] = await Promise.all([
      api.discordStatus(), api.discordDrafts(), api.discordHistory(50),
    ]);
    setStatus(s); setDrafts(d.drafts || []); setHistory(h.history || []);
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      const r = await api.discordPreview(cleanPayload(form));
      setPreview(r.payload);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const doSendTest = async () => {
    if (!window.confirm("Inviare un messaggio di TEST al canale Discord?")) return;
    setBusy(true);
    try {
      const r = await api.discordTest();
      if (r.sent) toast.success("Test inviato su Discord");
      else toast.error(`Test fallito: ${r.reason || "errore sconosciuto"}`);
      await refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); }
  };

  const doSend = async () => {
    if (!form.title.trim()) { toast.error("Il campo Titolo è obbligatorio"); return; }
    setBusy(true);
    try {
      const r = await api.discordSend(cleanPayload(form), activeDraftId);
      if (r.sent) {
        toast.success("Messaggio inviato su Discord");
        setForm(emptyPayload());
        setActiveDraftId(null);
        setPreview(null);
      } else {
        toast.error(`Invio fallito: ${r.reason || "errore sconosciuto"}`);
      }
      await refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); setConfirmSend(false); }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      if (activeDraftId) {
        await api.discordDraftUpdate(activeDraftId, cleanPayload(form));
        toast.success("Bozza aggiornata");
      } else {
        const d = await api.discordDraftCreate(cleanPayload(form));
        setActiveDraftId(d.draft_id);
        toast.success("Bozza salvata");
      }
      const list = await api.discordDrafts();
      setDrafts(list.drafts || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const loadDraft = (d) => {
    setActiveDraftId(d.draft_id);
    setForm({ ...emptyPayload(), ...(d.payload || {}) });
    setPreview(null);
  };
  const newDraft = () => { setActiveDraftId(null); setForm(emptyPayload()); setPreview(null); };
  const duplicateDraft = async (draft_id) => {
    try { await api.discordDraftDuplicate(draft_id);
      toast.success("Bozza duplicata");
      const list = await api.discordDrafts();
      setDrafts(list.drafts || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Duplicate failed"); }
  };
  const deleteDraft = async (draft_id) => {
    if (!window.confirm("Eliminare questa bozza?")) return;
    try {
      await api.discordDraftDelete(draft_id);
      if (activeDraftId === draft_id) newDraft();
      const list = await api.discordDrafts();
      setDrafts(list.drafts || []);
      toast.success("Bozza eliminata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // ── Changes list controls ──
  const addChange = () => setForm({ ...form, changes: [...form.changes, ""] });
  const setChange = (i, v) => setForm({ ...form, changes: form.changes.map((c, idx) => idx === i ? v : c) });
  const removeChange = (i) => setForm({ ...form, changes: form.changes.filter((_, idx) => idx !== i) });
  const moveChange = (i, dir) => {
    const arr = [...form.changes];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setForm({ ...form, changes: arr });
  };

  if (gate === "checking") {
    return <div className="min-h-screen flex items-center justify-center bg-[#050505] text-[#8c8c8c] font-heading tracking-widest">
      <span className="ember-pulse">SUMMONING...</span>
    </div>;
  }
  if (gate === "denied") {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505] text-[#8c8c8c] font-heading tracking-widest gap-4 px-4">
      <div className="text-[#E33333] text-lg">Accesso negato</div>
      <div className="font-body text-xs text-[#666]">Solo amministratori possono aprire questa pagina.</div>
      <button data-testid="admin-discord-back-denied" onClick={() => navigate("/game")}
        className="px-4 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase hover:bg-[#B89C3A]/10">
        Torna al gioco
      </button>
    </div>;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-[#E2DFD8]" data-testid="admin-discord-page">
      {/* Header */}
      <div className="border-b border-[#262626] px-4 sm:px-6 py-3 flex items-center gap-3 sticky top-0 bg-[#050505] z-10">
        <button data-testid="admin-discord-back" onClick={() => navigate("/game")}
          className="text-[#666] hover:text-[#E2DFD8]"><ArrowLeft className="w-4 h-4" /></button>
        <span className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A]">Gestione Discord</span>
        <button data-testid="admin-discord-refresh" onClick={refreshAll}
          className="ml-auto text-[#666] hover:text-[#E5C34A]"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
        {/* STATUS */}
        <StatusHeader status={status} />

        {/* FORM + PREVIEW */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4">
          <div className="panel p-4 space-y-3" data-testid="admin-discord-form">
            <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] flex items-center gap-2">
              {activeDraftId ? <>Bozza attiva · <span className="text-[#E5C34A]">{activeDraftId}</span></> : "Nuovo aggiornamento"}
              <button data-testid="admin-discord-new" onClick={newDraft}
                className="ml-auto font-mono text-[9px] text-[#B89C3A] hover:text-[#E5C34A]">+ Nuovo</button>
            </div>

            {/* Row: type + status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Tipo aggiornamento">
                <select data-testid="form-update-type" value={form.update_type}
                  onChange={(e) => setForm({ ...form, update_type: e.target.value, color: null })}
                  className="input-dark">
                  {(status?.options?.update_types || []).map(o =>
                    <option key={o.key} value={o.key}>{o.emoji} {o.label}</option>
                  )}
                </select>
              </Field>
              <Field label="Stato">
                <select data-testid="form-status" value={form.status || "Pubblicato"}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="input-dark">
                  {(status?.options?.statuses || ["Pubblicato"]).map(s =>
                    <option key={s} value={s}>{s}</option>
                  )}
                </select>
              </Field>
            </div>

            {/* Row: title + version */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
              <Field label="Titolo *">
                <input data-testid="form-title" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Es. Nuova patch pubblicata"
                  className="input-dark" />
              </Field>
              <Field label="Versione">
                <input data-testid="form-version" value={form.version || ""}
                  onChange={(e) => setForm({ ...form, version: e.target.value })}
                  placeholder="v1.4.2"
                  className="input-dark font-mono" />
              </Field>
            </div>

            {/* Description */}
            <Field label="Descrizione">
              <textarea data-testid="form-description" value={form.description || ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="input-dark resize-none"
                placeholder="Breve testo introduttivo" />
            </Field>

            {/* Changes */}
            <Field label="Modifiche effettuate">
              <div className="space-y-1" data-testid="form-changes">
                {form.changes.length === 0 && (
                  <div className="text-[10px] italic text-[#666] px-1">Aggiungi almeno una voce con "+ Aggiungi modifica"</div>
                )}
                {form.changes.map((c, i) => (
                  <div key={i} className="flex gap-1" data-testid={`form-change-${i}`}>
                    <input value={c} onChange={(e) => setChange(i, e.target.value)}
                      className="input-dark flex-1" placeholder={`Modifica #${i + 1}`} />
                    <button onClick={() => moveChange(i, -1)} disabled={i === 0}
                      className="px-2 border border-[#262626] text-[#666] hover:text-[#E5C34A] disabled:opacity-20"
                      data-testid={`form-change-up-${i}`}><ArrowUp className="w-3 h-3" /></button>
                    <button onClick={() => moveChange(i, +1)} disabled={i === form.changes.length - 1}
                      className="px-2 border border-[#262626] text-[#666] hover:text-[#E5C34A] disabled:opacity-20"
                      data-testid={`form-change-down-${i}`}><ArrowDown className="w-3 h-3" /></button>
                    <button onClick={() => removeChange(i)}
                      className="px-2 border border-[#262626] text-[#E33333] hover:bg-[#8A0303]/10"
                      data-testid={`form-change-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
                <button data-testid="form-add-change" onClick={addChange}
                  className="mt-1 font-mono text-[10px] text-[#B89C3A] hover:text-[#E5C34A] flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Aggiungi modifica
                </button>
              </div>
            </Field>

            {/* Row: url + timestamp */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="URL applicazione">
                <input data-testid="form-app-url" value={form.app_url || ""}
                  onChange={(e) => setForm({ ...form, app_url: e.target.value })}
                  placeholder={status?.app_url || "https://grim-walker.com"}
                  className="input-dark font-mono text-xs" />
              </Field>
              <Field label="Data e ora (ISO, opzionale)">
                <input data-testid="form-timestamp" value={form.timestamp_iso || ""}
                  onChange={(e) => setForm({ ...form, timestamp_iso: e.target.value })}
                  placeholder="Auto (adesso)"
                  className="input-dark font-mono text-xs" />
              </Field>
            </div>

            {/* Row: color override + footer */}
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3">
              <Field label="Colore Embed">
                <div className="flex items-center gap-2">
                  <input data-testid="form-color" type="color" value={effectiveColorHex}
                    onChange={(e) => setForm({ ...form, color: parseInt(e.target.value.slice(1), 16) })}
                    className="w-10 h-8 border border-[#262626] cursor-pointer bg-transparent" />
                  <button onClick={() => setForm({ ...form, color: null })}
                    className="font-mono text-[9px] text-[#666] hover:text-[#E5C34A]">Reset default</button>
                </div>
              </Field>
              <Field label="Footer">
                <input data-testid="form-footer" value={form.footer || ""}
                  onChange={(e) => setForm({ ...form, footer: e.target.value })}
                  placeholder={status?.options?.default_footer || "Deploy Notification"}
                  className="input-dark" />
              </Field>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-[#262626]">
              <button data-testid="btn-preview" onClick={doPreview} disabled={busy}
                className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase hover:bg-[#B89C3A]/10 disabled:opacity-30 flex items-center gap-2">
                <Eye className="w-3 h-3" /> Anteprima
              </button>
              <button data-testid="btn-test" onClick={doSendTest} disabled={busy || !status?.webhook_configured}
                className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase hover:bg-[#B89C3A]/10 disabled:opacity-30 flex items-center gap-2">
                <TestTube2 className="w-3 h-3" /> Invia messaggio di test
              </button>
              <button data-testid="btn-save-draft" onClick={saveDraft} disabled={busy}
                className="px-3 py-2 border border-[#4A5D23] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10 disabled:opacity-30 flex items-center gap-2">
                <Save className="w-3 h-3" /> Salva bozza
              </button>
              <button data-testid="btn-send" onClick={() => setConfirmSend(true)}
                disabled={busy || !form.title.trim() || !status?.webhook_configured}
                className="ml-auto px-3 py-2 border border-[#E55A00] text-[#E55A00] font-heading text-[10px] tracking-widest uppercase hover:bg-[#E55A00]/10 disabled:opacity-30 flex items-center gap-2">
                <Send className="w-3 h-3" /> Invia a Discord
              </button>
            </div>
          </div>

          {/* PREVIEW */}
          <div className="space-y-3">
            <EmbedPreview data={preview} placeholderColor={effectiveColorHex} />
            <DraftsList drafts={drafts} activeId={activeDraftId}
              onLoad={loadDraft} onDuplicate={duplicateDraft} onDelete={deleteDraft} />
          </div>
        </div>

        {/* HISTORY */}
        <HistoryTable history={history} />
      </div>

      {/* CONFIRM SEND */}
      {confirmSend && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="confirm-send-modal">
          <div className="panel p-5 max-w-md w-full space-y-3">
            <div className="font-heading text-sm tracking-widest uppercase text-[#E5C34A]">Confermi invio?</div>
            <div className="font-body text-xs text-[#B0ADA5]">
              Sei sicuro di voler inviare questo aggiornamento al canale Discord?
              L'operazione è definitiva e verrà registrata nello storico.
            </div>
            <div className="flex gap-2 justify-end pt-3 border-t border-[#262626]">
              <button data-testid="confirm-cancel" onClick={() => setConfirmSend(false)}
                className="px-3 py-2 border border-[#262626] text-[#8c8c8c] font-heading text-[10px] tracking-widest uppercase hover:bg-[#262626]">Annulla</button>
              <button data-testid="confirm-send" onClick={doSend} disabled={busy}
                className="px-3 py-2 border border-[#E55A00] text-[#E55A00] font-heading text-[10px] tracking-widest uppercase hover:bg-[#E55A00]/10 disabled:opacity-30">Conferma invio</button>
            </div>
          </div>
        </div>
      )}

      {/* Utility class */}
      <style>{`
        .input-dark {
          width: 100%;
          background: #0a0a0a;
          border: 1px solid #333;
          padding: 6px 10px;
          font-size: 12px;
          color: #E2DFD8;
          border-radius: 0;
          outline: none;
        }
        .input-dark:focus { border-color: #B89C3A; }
      `}</style>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

function cleanPayload(f) {
  return {
    update_type: f.update_type,
    title: f.title || null,
    version: f.version || null,
    status: f.status || null,
    description: f.description || null,
    changes: (f.changes || []).filter(c => (c || "").trim()),
    app_url: f.app_url || null,
    color: f.color,
    footer: f.footer || null,
    timestamp_iso: f.timestamp_iso || null,
  };
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">{label}</div>
      {children}
    </label>
  );
}

function StatusHeader({ status }) {
  const configured = !!status?.webhook_configured;
  const envLabel = status?.deploy_env || "—";
  const last = status?.last;
  return (
    <div className="panel p-4" data-testid="admin-discord-status">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <Circle className={`w-3 h-3 ${configured ? "text-[#6E8B3D]" : "text-[#E33333]"}`}
            fill="currentColor" strokeWidth={0} />
          <div>
            <div className="font-heading text-xs tracking-widest uppercase text-[#8c8c8c]">Stato webhook</div>
            <div className="font-heading text-sm uppercase tracking-widest"
              data-testid="webhook-status-label">
              {configured
                ? <span className="text-[#6E8B3D]">Configurato</span>
                : <span className="text-[#E33333]">Non configurato</span>}
            </div>
          </div>
        </div>
        <div className="sm:ml-auto flex flex-wrap gap-3 sm:gap-6">
          <StatCell label="Ambiente" value={envLabel} />
          <StatCell label="Progetto" value={status?.project_name || "—"} />
          <StatCell label="Build" value={status?.current_commit_sha ? status.current_commit_sha.slice(0, 7) : "—"} mono />
        </div>
      </div>
      {last && (
        <div className="mt-3 pt-3 border-t border-[#262626] flex flex-wrap gap-3 sm:gap-6 font-mono text-[10px] tracking-widest uppercase">
          <span className="text-[#8c8c8c]">Ultimo invio:</span>
          <span className={last.status === "sent" ? "text-[#6E8B3D]" : "text-[#E33333]"}>
            {last.status === "sent" ? "Inviato" : "Fallito"} · {last.method}
          </span>
          <span className="text-[#666]">{new Date(last.sent_at).toLocaleString()}</span>
          {last.http_status && <span className="text-[#B89C3A]">HTTP {last.http_status}</span>}
          {last.error_reason && <span className="text-[#E33333]">· {last.error_reason}</span>}
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, mono }) {
  return (
    <div>
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">{label}</div>
      <div className={`text-xs uppercase tracking-widest ${mono ? "font-mono" : "font-heading"} text-[#E2DFD8]`}>{value}</div>
    </div>
  );
}

function EmbedPreview({ data, placeholderColor }) {
  if (!data) {
    return (
      <div className="panel p-4" data-testid="embed-preview-empty">
        <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-2">Anteprima</div>
        <div className="text-xs italic text-[#666]">Compila il form e premi <b className="text-[#E5C34A]">Anteprima</b> per vedere il messaggio.</div>
      </div>
    );
  }
  const embed = data.embeds?.[0] || {};
  const borderColor = "#" + (embed.color || 0x6E8B3D).toString(16).padStart(6, "0");
  return (
    <div className="panel p-4" data-testid="embed-preview">
      <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-2">Anteprima Discord</div>
      <div className="bg-[#2b2d31] p-3 text-[13px] font-sans text-[#dbdee1] rounded-sm"
        style={{ borderLeft: `4px solid ${borderColor}` }}>
        {embed.title && <div className="font-semibold text-white mb-1">{embed.title}</div>}
        {embed.description && <div className="text-[#dbdee1] whitespace-pre-wrap mb-2">{embed.description}</div>}
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-2">
          {(embed.fields || []).map((f, i) => (
            <div key={i} className={f.inline ? "" : "col-span-2"}>
              <div className="text-[11px] font-semibold text-white">{f.name}</div>
              <div className="text-[12px] whitespace-pre-wrap break-words">{f.value}</div>
            </div>
          ))}
        </div>
        {embed.footer && (
          <div className="text-[11px] text-[#a3a6aa] pt-1 border-t border-[#3f4147] mt-2">
            {embed.footer.text}{embed.timestamp && ` · ${new Date(embed.timestamp).toLocaleString()}`}
          </div>
        )}
      </div>
    </div>
  );
}

function DraftsList({ drafts, activeId, onLoad, onDuplicate, onDelete }) {
  return (
    <div className="panel p-4" data-testid="drafts-list">
      <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-2">
        Bozze ({drafts.length})
      </div>
      {drafts.length === 0 && (
        <div className="text-xs italic text-[#666]">Nessuna bozza. Salvane una per riprenderla più tardi.</div>
      )}
      <div className="space-y-1 max-h-80 overflow-y-auto">
        {drafts.map(d => (
          <div key={d.draft_id}
            className={`border p-2 text-xs flex flex-col gap-1 ${d.draft_id === activeId ? "border-[#E5C34A] bg-[#B89C3A]/5" : "border-[#262626]"}`}
            data-testid={`draft-${d.draft_id}`}>
            <div className="flex items-center gap-2">
              <span className="flex-1 truncate text-[#E2DFD8]">{d.payload?.title || <i className="text-[#666]">Senza titolo</i>}</span>
              <span className="font-mono text-[9px] tracking-widest uppercase text-[#B89C3A]">{d.payload?.update_type}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[9px] text-[#666]">{new Date(d.updated_at).toLocaleString()}</span>
              <button onClick={() => onLoad(d)} data-testid={`draft-load-${d.draft_id}`}
                className="ml-auto font-mono text-[9px] text-[#B89C3A] hover:text-[#E5C34A]">Apri</button>
              <button onClick={() => onDuplicate(d.draft_id)} data-testid={`draft-dup-${d.draft_id}`}
                className="text-[#B89C3A] hover:text-[#E5C34A]"><Copy className="w-3 h-3 inline" /></button>
              <button onClick={() => onDelete(d.draft_id)} data-testid={`draft-del-${d.draft_id}`}
                className="text-[#E33333] hover:text-[#8A0303]"><Trash2 className="w-3 h-3 inline" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryTable({ history }) {
  return (
    <div className="panel p-4" data-testid="history-table">
      <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] mb-2">
        Storico notifiche Discord ({history.length})
      </div>
      {history.length === 0 && <div className="text-xs italic text-[#666]">Nessun invio registrato.</div>}
      <div className="overflow-x-auto">
        {history.length > 0 && (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left border-b border-[#262626] font-mono text-[9px] uppercase tracking-widest text-[#666]">
                <th className="py-1 pr-2">Data</th>
                <th className="py-1 pr-2">Tipo</th>
                <th className="py-1 pr-2">Titolo</th>
                <th className="py-1 pr-2">Versione</th>
                <th className="py-1 pr-2">Metodo</th>
                <th className="py-1 pr-2">Stato</th>
                <th className="py-1 pr-2">Admin</th>
                <th className="py-1 pr-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.history_id} className="border-b border-[#1a1a1a]" data-testid={`hist-${h.history_id}`}>
                  <td className="py-1 pr-2 font-mono text-[10px] text-[#8c8c8c] whitespace-nowrap">
                    {new Date(h.sent_at).toLocaleString()}
                  </td>
                  <td className="py-1 pr-2 font-mono text-[10px] text-[#B89C3A]">{h.payload_summary?.type || "—"}</td>
                  <td className="py-1 pr-2 truncate max-w-[240px]">{h.payload_summary?.title || "—"}</td>
                  <td className="py-1 pr-2 font-mono text-[10px] text-[#B0ADA5]">{h.payload_summary?.version || "—"}</td>
                  <td className="py-1 pr-2 font-mono text-[10px] uppercase text-[#8c8c8c]">{h.method}</td>
                  <td className={`py-1 pr-2 font-mono text-[10px] uppercase ${h.status === "sent" ? "text-[#6E8B3D]" : "text-[#E33333]"}`}>
                    {h.status}
                  </td>
                  <td className="py-1 pr-2 font-mono text-[10px] text-[#666]">{h.admin_name || "system"}</td>
                  <td className="py-1 pr-2 font-mono text-[9px] text-[#666]">
                    {h.http_status ? `HTTP ${h.http_status}` : ""}{h.error_reason ? ` · ${h.error_reason}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
